export type DeveloperLogLevel = "debug" | "info" | "log" | "warn" | "error";

export type DeveloperLogSource =
  | "console"
  | "window-error"
  | "unhandled-rejection"
  | "manual";

export interface DeveloperLogEntry {
  id: string;
  timestamp: string;
  level: DeveloperLogLevel;
  source: DeveloperLogSource;
  path: string;
  message: string;
  details: string | null;
}

const LOG_STORAGE_KEY = "macrotracker:developer-logs";
const MAX_LOG_ENTRIES = 200;
const MAX_MESSAGE_LENGTH = 320;
const MAX_DETAILS_LENGTH = 8000;
const MAX_INLINE_VALUE_LENGTH = 120;
const MAX_SERIALIZE_DEPTH = 4;
const MAX_SERIALIZE_KEYS = 20;
const MAX_SERIALIZE_ITEMS = 20;

const LOG_LEVELS: DeveloperLogLevel[] = ["debug", "info", "log", "warn", "error"];

type ConsoleMethod = (...args: unknown[]) => void;
type MutableConsole = Console & Record<DeveloperLogLevel, ConsoleMethod>;

const mutableConsole = console as MutableConsole;

const defaultConsoleMethods: Record<DeveloperLogLevel, ConsoleMethod> = {
  debug: console.debug.bind(console),
  info: console.info.bind(console),
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

let activeConsoleMethods: Record<DeveloperLogLevel, ConsoleMethod> = {
  ...defaultConsoleMethods,
};

let initialized = false;
let isPersisting = false;
let cachedEntries: DeveloperLogEntry[] | null = null;
let memoryEntries: DeveloperLogEntry[] = [];

const hasLocalStorage = (): boolean => {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
};

const truncate = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) {
    return value;
  }

  const sliceLength = Math.max(0, maxLength - 3);
  return `${value.slice(0, sliceLength)}...`;
};

const isDeveloperLogLevel = (value: unknown): value is DeveloperLogLevel => {
  return typeof value === "string" && LOG_LEVELS.includes(value as DeveloperLogLevel);
};

const isDeveloperLogSource = (value: unknown): value is DeveloperLogSource => {
  return (
    value === "console" ||
    value === "window-error" ||
    value === "unhandled-rejection" ||
    value === "manual"
  );
};

const normalizeStoredEntry = (value: unknown): DeveloperLogEntry | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const candidate = value as Partial<DeveloperLogEntry>;
  if (
    typeof candidate.id !== "string" ||
    typeof candidate.timestamp !== "string" ||
    !isDeveloperLogLevel(candidate.level) ||
    !isDeveloperLogSource(candidate.source) ||
    typeof candidate.path !== "string" ||
    typeof candidate.message !== "string"
  ) {
    return null;
  }

  return {
    id: candidate.id,
    timestamp: candidate.timestamp,
    level: candidate.level,
    source: candidate.source,
    path: candidate.path,
    message: candidate.message,
    details: typeof candidate.details === "string" ? candidate.details : null,
  };
};

const loadEntriesFromStorage = (): DeveloperLogEntry[] => {
  if (!hasLocalStorage()) {
    return [...memoryEntries];
  }

  try {
    const raw = window.localStorage.getItem(LOG_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((entry) => normalizeStoredEntry(entry))
      .filter((entry): entry is DeveloperLogEntry => entry !== null)
      .slice(0, MAX_LOG_ENTRIES);
  } catch {
    return [];
  }
};

const persistEntries = (entries: DeveloperLogEntry[]): void => {
  const normalized = entries.slice(0, MAX_LOG_ENTRIES);
  cachedEntries = normalized;

  if (!hasLocalStorage()) {
    memoryEntries = normalized;
    return;
  }

  try {
    if (normalized.length === 0) {
      window.localStorage.removeItem(LOG_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    // Ignore storage failures so the logger never breaks the app.
  }
};

const getCachedEntries = (): DeveloperLogEntry[] => {
  if (cachedEntries === null) {
    cachedEntries = loadEntriesFromStorage();
  }

  return cachedEntries;
};

const createLogId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getCurrentPath = (): string => {
  if (typeof window === "undefined") {
    return "/";
  }

  const { pathname, search, hash } = window.location;
  const fullPath = `${pathname}${search}${hash}`;
  return fullPath || "/";
};

const getObjectLabel = (value: object): string => {
  return value.constructor?.name ?? "Object";
};

const serializeValue = (
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>(),
): unknown => {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (typeof value === "bigint") {
    return `${value.toString()}n`;
  }

  if (typeof value === "symbol") {
    return value.toString();
  }

  if (typeof value === "function") {
    return `[Function ${value.name || "anonymous"}]`;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof URL) {
    return value.toString();
  }

  if (value instanceof Error) {
    const serializedError: Record<string, unknown> = {
      name: value.name,
      message: value.message,
    };

    if (value.stack) {
      serializedError.stack = value.stack;
    }

    if ("cause" in value && value.cause !== undefined) {
      serializedError.cause = serializeValue(value.cause, depth + 1, seen);
    }

    for (const [key, nestedValue] of Object.entries(value)) {
      if (key in serializedError) {
        continue;
      }
      serializedError[key] = serializeValue(nestedValue, depth + 1, seen);
    }

    return serializedError;
  }

  if (typeof Element !== "undefined" && value instanceof Element) {
    return `<${value.tagName.toLowerCase()}>`;
  }

  if (typeof value !== "object") {
    return String(value);
  }

  if (seen.has(value)) {
    return "[Circular]";
  }

  if (depth >= MAX_SERIALIZE_DEPTH) {
    return `[${getObjectLabel(value)}]`;
  }

  seen.add(value);

  if (Array.isArray(value)) {
    const items = value
      .slice(0, MAX_SERIALIZE_ITEMS)
      .map((item) => serializeValue(item, depth + 1, seen));

    if (value.length > MAX_SERIALIZE_ITEMS) {
      items.push(`[+${value.length - MAX_SERIALIZE_ITEMS} more items]`);
    }

    seen.delete(value);
    return items;
  }

  const entries = Object.entries(value as Record<string, unknown>);
  const result: Record<string, unknown> = {};

  for (const [key, nestedValue] of entries.slice(0, MAX_SERIALIZE_KEYS)) {
    result[key] = serializeValue(nestedValue, depth + 1, seen);
  }

  if (entries.length > MAX_SERIALIZE_KEYS) {
    result.__truncatedKeys = entries.length - MAX_SERIALIZE_KEYS;
  }

  if (Object.keys(result).length === 0) {
    seen.delete(value);
    return `[${getObjectLabel(value)}]`;
  }

  seen.delete(value);
  return result;
};

const toInlineValue = (value: unknown): string => {
  if (value === undefined) {
    return "undefined";
  }

  if (value === null) {
    return "null";
  }

  if (typeof value === "string") {
    return value;
  }

  if (value instanceof Error) {
    return `${value.name}: ${value.message}`;
  }

  try {
    const serialized = serializeValue(value);
    if (typeof serialized === "string") {
      return serialized;
    }
    return JSON.stringify(serialized) ?? String(serialized);
  } catch {
    return String(value);
  }
};

const buildMessage = (args: unknown[]): string => {
  if (args.length === 0) {
    return "Console entry";
  }

  const parts = args.map((arg) => truncate(toInlineValue(arg), MAX_INLINE_VALUE_LENGTH));
  const joined = parts.filter((part) => part.length > 0).join(" ");
  return truncate(joined || "Console entry", MAX_MESSAGE_LENGTH);
};

const buildDetails = (args: unknown[]): string | null => {
  if (args.length === 0) {
    return null;
  }

  if (args.length === 1 && typeof args[0] === "string") {
    return null;
  }

  try {
    const serializedArgs = args.map((arg) => serializeValue(arg));
    const payload = serializedArgs.length === 1 ? serializedArgs[0] : serializedArgs;
    const json = JSON.stringify(payload, null, 2);
    if (!json) {
      return null;
    }

    return truncate(json, MAX_DETAILS_LENGTH);
  } catch {
    return null;
  }
};

const appendEntry = (
  level: DeveloperLogLevel,
  args: unknown[],
  source: DeveloperLogSource,
): void => {
  if (isPersisting) {
    return;
  }

  isPersisting = true;
  try {
    const nextEntry: DeveloperLogEntry = {
      id: createLogId(),
      timestamp: new Date().toISOString(),
      level,
      source,
      path: getCurrentPath(),
      message: buildMessage(args),
      details: buildDetails(args),
    };

    persistEntries([nextEntry, ...getCachedEntries()]);
  } finally {
    isPersisting = false;
  }
};

const handleWindowError = (event: ErrorEvent): void => {
  appendEntry(
    "error",
    [
      event.message || "Unhandled window error",
      {
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
        error: event.error ?? null,
      },
    ],
    "window-error",
  );
};

const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
  appendEntry(
    "error",
    ["Unhandled promise rejection", event.reason],
    "unhandled-rejection",
  );
};

export const initializeDeveloperLogging = (): void => {
  if (initialized) {
    return;
  }

  activeConsoleMethods = {
    debug: console.debug.bind(console),
    info: console.info.bind(console),
    log: console.log.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
  };

  for (const level of LOG_LEVELS) {
    const original = activeConsoleMethods[level];

    mutableConsole[level] = (...args: unknown[]) => {
      try {
        original(...args);
      } finally {
        appendEntry(level, args, "console");
      }
    };
  }

  if (typeof window !== "undefined") {
    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
  }

  initialized = true;
};

export const captureDeveloperLog = (
  level: DeveloperLogLevel,
  ...args: unknown[]
): void => {
  appendEntry(level, args, "manual");
};

export const listDeveloperLogs = (): DeveloperLogEntry[] => {
  return [...getCachedEntries()];
};

export const clearDeveloperLogs = (): void => {
  persistEntries([]);
};

export const resetDeveloperLoggingForTests = (): void => {
  for (const level of LOG_LEVELS) {
    mutableConsole[level] = defaultConsoleMethods[level];
  }

  if (typeof window !== "undefined") {
    window.removeEventListener("error", handleWindowError);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  }

  initialized = false;
  isPersisting = false;
  cachedEntries = null;
  memoryEntries = [];
  activeConsoleMethods = {
    ...defaultConsoleMethods,
  };

  if (hasLocalStorage()) {
    try {
      window.localStorage.removeItem(LOG_STORAGE_KEY);
    } catch {
      // Ignore storage cleanup failures in tests.
    }
  }
};

export const developerLogService = {
  initialize: initializeDeveloperLogging,
  capture: captureDeveloperLog,
  list: listDeveloperLogs,
  clear: clearDeveloperLogs,
};
